/* Fail-closed submit readiness for product forms with native option consumers. */
(function (global, document) {
  'use strict';
  if (global.FSNativeReadiness) return;

  const formSelector = 'form[data-fs-native-required="true"]';
  const defaultMessage = 'Product options are still loading. Refresh the page before adding this item.';
  const unavailableMessage = 'We could not load the options needed to price this item. Refresh the page or contact us before ordering.';
  const submitSelector = 'button[type="submit"],input[type="submit"],button[name="add"],input[name="add"]';
  const messageNodes = new WeakMap();
  const observers = new WeakMap();

  function submitButtons(form) {
    return [...form.querySelectorAll(submitSelector)];
  }

  function messageNode(form) {
    let node = form.querySelector('[data-fs-native-readiness-message]') || messageNodes.get(form);
    if (node) return node;
    node = document.createElement('p');
    node.dataset.fsNativeReadinessMessage = '';
    node.className = 'fs-native-readiness-message';
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');
    node.hidden = true;
    const firstButton = form.querySelector(submitSelector);
    const anchor = firstButton?.closest('.t4s-product-form__buttons,.t4s-product-form__submit,.product-form__buttons') || firstButton?.parentElement;
    if (anchor?.parentNode) anchor.parentNode.insertBefore(node, anchor);
    else form.append(node);
    messageNodes.set(form, node);
    return node;
  }

  function show(form, text) {
    const node = messageNode(form);
    node.textContent = text || defaultMessage;
    node.hidden = false;
  }

  function hide(form) {
    const node = form.querySelector('[data-fs-native-readiness-message]') || messageNodes.get(form);
    if (node) { node.textContent = ''; node.hidden = true; }
  }

  function setButtons(form, disabled) {
    submitButtons(form).forEach(button => {
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    });
  }

  function readyButtons(form, submitEnabled) {
    submitButtons(form).forEach(button => {
      const businessDisabled = button.hasAttribute('data-fs-native-business-disabled');
      const cartLocked = form.dataset.fsNativeCartLocked === 'true';
      const disabled = businessDisabled || cartLocked || !submitEnabled;
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    });
  }

  function applyToNewButton(form, button) {
    const businessDisabled = button.hasAttribute('data-fs-native-business-disabled');
    const disabled = form.dataset.fsNativeState !== 'ready'
      || form.dataset.fsNativeSubmitEnabled !== 'true'
      || form.dataset.fsNativeCartLocked === 'true'
      || businessDisabled;
    button.disabled = disabled;
    button.setAttribute('aria-disabled', String(disabled));
  }

  function observeLateButtons(form) {
    if (observers.has(form)) return;
    const observer = new global.MutationObserver(records => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (!(node instanceof global.Element)) continue;
          if (node.matches(submitSelector)) applyToNewButton(form, node);
          node.querySelectorAll?.(submitSelector).forEach(button => applyToNewButton(form, button));
        }
      }
    });
    observer.observe(form, {childList:true,subtree:true});
    observers.set(form, observer);
  }

  function watchPrice(root, selector) {
    if (!(root instanceof global.Element) || typeof selector !== 'string' || !selector) return null;
    const scope = root.closest('[data-product-featured]')
      || root.closest('.t4s-product__info-container')
      || document;
    let expectedText = null;
    const currentPrice = () => scope.querySelector(selector);
    const reconcile = () => {
      if (expectedText === null) return;
      const price = currentPrice();
      if (price && price.textContent.trim() !== expectedText) price.textContent = expectedText;
    };
    const observer = new global.MutationObserver(reconcile);
    observer.observe(scope, {childList:true,characterData:true,subtree:true});
    const afterThemeLoad = () => global.requestAnimationFrame(reconcile);
    if (document.readyState === 'complete') afterThemeLoad();
    else global.addEventListener('load', afterThemeLoad, {once:true});
    return Object.freeze({
      set(value) {
        expectedText = String(value ?? '');
        reconcile();
      },
      refresh: reconcile,
    });
  }

  function begin(form, options) {
    if (!(form instanceof global.HTMLFormElement)) return false;
    if (options?.mode !== 'native') return false;
    const expectedMode = form.dataset.fsNativeMode;
    // Legacy YMQ and unmapped forms are explicitly outside this gate. A
    // native consumer must not convert their server-rendered false marker.
    if (expectedMode && expectedMode !== 'native' && form.dataset.fsNativeRequired !== 'true') return false;
    if (!form.matches(formSelector)) form.setAttribute('data-fs-native-required', 'true');
    const mode = options?.mode;
    if (!mode || (expectedMode && mode !== expectedMode)) {
      form.dataset.fsNativeState = 'failed';
      setButtons(form, true);
      show(form, unavailableMessage);
      return false;
    }
    form.dataset.fsNativeState = 'pending';
    form.dataset.fsNativeStartedMode = mode;
    form.dataset.fsNativeSubmitEnabled = 'false';
    setButtons(form, true);
    observeLateButtons(form);
    return true;
  }

  function ready(form, options) {
    if (!(form instanceof global.HTMLFormElement) || !form.matches(formSelector)) return false;
    const mode = options?.mode;
    const submitEnabled = options?.submitEnabled;
    if (form.dataset.fsNativeState !== 'pending' || !mode || mode !== form.dataset.fsNativeMode || mode !== form.dataset.fsNativeStartedMode || typeof submitEnabled !== 'boolean') {
      fail(form, unavailableMessage);
      return false;
    }
    form.dataset.fsNativeState = 'ready';
    form.dataset.fsNativeSubmitEnabled = String(submitEnabled);
    readyButtons(form, submitEnabled);
    hide(form);
    return true;
  }

  function setSubmitEnabled(form, options) {
    if (!(form instanceof global.HTMLFormElement) || !form.matches(formSelector)) return false;
    if (form.dataset.fsNativeState !== 'ready' || !options?.mode || options.mode !== form.dataset.fsNativeMode || typeof options.submitEnabled !== 'boolean') return false;
    form.dataset.fsNativeSubmitEnabled = String(options.submitEnabled);
    readyButtons(form, options.submitEnabled);
    return true;
  }

  function setCartLocked(form, locked) {
    if (!(form instanceof global.HTMLFormElement) || !form.matches(formSelector)) return false;
    form.dataset.fsNativeCartLocked = String(Boolean(locked));
    if (form.dataset.fsNativeState === 'ready') readyButtons(form, form.dataset.fsNativeSubmitEnabled === 'true');
    return true;
  }

  function fail(form, message) {
    if (!(form instanceof global.HTMLFormElement)) return false;
    form.setAttribute('data-fs-native-required', 'true');
    form.dataset.fsNativeState = 'failed';
    form.dataset.fsNativeSubmitEnabled = 'false';
    setButtons(form, true);
    observeLateButtons(form);
    show(form, message || unavailableMessage);
    return true;
  }

  // Kalles re-adds data-atc-form while processing variant changes, including
  // on native forms that omitted the attribute in server-rendered markup. Its
  // delegated click handler can then cancel the browser submit before the
  // native family submit handler runs. Route only explicitly native buttons
  // through requestSubmit at capture time; legacy/YMQ forms keep Kalles' path.
  document.addEventListener('click', event => {
    const origin = event.target instanceof global.Element ? event.target : null;
    const button = origin?.closest(submitSelector);
    const form = button?.form || button?.closest('form');
    if (!button || !(form instanceof global.HTMLFormElement) || !form.matches(formSelector)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const state = form.dataset.fsNativeState;
    const available = form.dataset.fsNativeSubmitEnabled === 'true';
    const cartLocked = form.dataset.fsNativeCartLocked === 'true';
    const businessDisabled = button.hasAttribute('data-fs-native-business-disabled');
    if (state !== 'ready' || !available || cartLocked || businessDisabled) {
      const message = state === 'failed' ? unavailableMessage
        : state !== 'ready' ? defaultMessage
        : cartLocked ? 'Your cart request is still being checked. Review the cart status before adding again.'
        : businessDisabled ? 'This product option is not available. Choose another option before adding it to your cart.'
        : 'This product option is not available. Choose another option before adding it to your cart.';
      show(form, message);
      return;
    }

    // Do not call form.submit(): requestSubmit preserves browser validation
    // and the native family form-submit listener. A missing API or invalid
    // submitter stays fail-closed instead of falling through to Kalles.
    if (typeof form.requestSubmit !== 'function') {
      show(form, unavailableMessage);
      return;
    }
    try { form.requestSubmit(button); }
    catch (_) { show(form, unavailableMessage); }
  }, true);

  // Install before the family inline scripts. Document capture runs before
  // listeners on the form, including an Enter-key implicit submit.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof global.HTMLFormElement) || !form.matches(formSelector)) return;
    if (form.dataset.fsNativeState === 'ready') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const existing = form.querySelector('[data-fs-native-readiness-message]')?.textContent?.trim();
    show(form, form.dataset.fsNativeState === 'failed' ? (existing || unavailableMessage) : defaultMessage);
  }, true);

  global.FSNativeReadiness = Object.freeze({begin, ready, setSubmitEnabled, setCartLocked, fail, watchPrice});
})(window, document);
